/**
 * @fileoverview Input/output schemas for world-object-ideation-v3.
 *
 * This agent generates ideas for significant objects (artifacts, items).
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { WorldObjectIdeaV3Schema, WorldObjectElementEnum } from '@/domains/world-building/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldObjects.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.characters.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.initialChapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  targetElement: WorldObjectElementEnum.optional().describe(
    'Optional filter to generate only a specific element type (artifact or item). If not provided, generates a mix of both.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output schema without tempId and source (added by the hook).
 */
const WorldObjectIdeaOutputSchema = WorldObjectIdeaV3Schema.omit({
  tempId: true,
  source: true,
});

export const OutputSchema = z.object({
  objects: z.array(WorldObjectIdeaOutputSchema).describe('An array of generated world object ideas.'),
});

export type Output = z.infer<typeof OutputSchema>;
